import React, {useState} from 'react';
import {Button, Card, Col, DatePicker, message, Row, Table, Form} from "antd";
import {useSelector} from "react-redux";
import {useNewAlarmReportMutation} from "../../../slices/rootApiSlices/reportApiSlice";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import SelectVehicle from "./SelectVehicleAll";
import {DownloadOutlined, FilePdfOutlined, FileTextOutlined} from "@ant-design/icons";
import AlarmMapModal from "../../../components/AlarmMapModal";
import moment from "moment";

const AlarmReport = () => {

    const [tableData, setTableData] = useState([]);
    const [infoData, setInfoData] = useState({});
    const {RangePicker} = DatePicker;
    const [endDate, setEndDate] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [vehicle_id, setVehicleID] = useState('');
    const [selectedGps, setSelectedGps] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const {user} = useSelector((state) => state.auth);
    const [alarmHandle, {isLoading}, error] = useNewAlarmReportMutation();

    function disabledDate(current) {
        const tomorrow = moment().add(1, 'days').startOf('day');
        return current && current >= tomorrow;
    }

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
        total: 0,
    });

    const showModal = (gps) => {
        if (gps) {
            setSelectedGps(gps);
            setIsModalVisible(true);
        } else {
            message.warning('Konum bulunamadı.');
        }
    };

    const closeModal = () => {
        setIsModalVisible(false);
    };

    const handleQuery = async () => {
        let alarmData;
        if (vehicle_id === true) {
            alarmData = {
                startDate,
                endDate,
                token: user?.token,
                all: true,
            };
        } else {
            alarmData = {
                id: vehicle_id,
                token: user?.token,
                startDate,
                endDate,
            };
        }
        try {
            const res = await alarmHandle(alarmData).unwrap();
            setInfoData(res?.info);
            setTableData(res?.data);
            message.success(res?.message, 4);
        } catch (e) {
            message.error(e?.data?.messageTR || 'Bir hata oluştu. Lütfen bir süre sonra tekrar deneyiniz.');
        }
    };

    const getUniquePlateNumbers = () => {
        const plateNumbers = tableData.map(record => record.plateNumber);
        return [...new Set(plateNumbers)].map(plate => ({text: plate, value: plate}));
    };

    const onPageChange = (page, pageSize) => {
        setPagination({current: page, pageSize: pageSize});
    };

    function handleDateChange(date, dateString) {
        const adjustDate = (dateStr) => {
            const date = new Date(dateStr.replace(' ', 'T') + '.000Z');
            date.setHours(date.getHours() - 3); // Subtract 3 hours
            return date.toISOString().slice(0, 19).replace('T', ' ');
        };

        const startDate = adjustDate(dateString[0]);
        const endDate = adjustDate(dateString[1]);

        setStartDate(startDate);
        setEndDate(endDate);
    }

    const columns = [
        {
            title: 'Oluşturulma Tarihi',
            dataIndex: 'date',
            key: 'date',
            render: (text) => {
                const date = new Date(text);
                date.setHours(date.getHours() + 3);
                const adjustedDate = date.toISOString().slice(0, 19).replace('T', ' '); // Adjust formatting if needed
                return <span>{adjustedDate}</span>;
            },
        },
        {
            title: 'Kapı No',
            dataIndex: 'operationNumber',
            key: 'operationNumber',
        },
        {
            title: 'Araç Plaka Numarası',
            dataIndex: 'plateNumber',
            key: 'plateNumber',
            filters: getUniquePlateNumbers(),
            filterSearch: true,
            onFilter: (value, record) => record.plateNumber.includes(value),
        },
        {
            title: 'Arıza Tipi',
            dataIndex: 'value',
            key: 'value',
            width: '10%',
            sorter: (a, b) => a.value.localeCompare(b.value),
            onFilter: (value, record) => record.value.indexOf(value) === 0,
            filters: [
                {text: "Uyarı", value: "CONDITION_YELLOW"},
                {text: "Arıza", value: "CONDITION_RED"},
            ],
            render: (text) => (
                <span>
                    {text === 'CONDITION_YELLOW' ? 'Uyarı' :
                        text === 'CONDITION_RED' ? 'Arıza' :
                            <del>{text}</del>
                    }
                </span>
            ),
        },
        {
            title: 'Arıza Detay',
            dataIndex: 'name',
            showSorterTooltip: {
                target: 'full-header',
            },
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            onFilter: (value, record) => record.name.indexOf(value) === 0,
            filters: [
                {text: "Uzun Far", value: "Uzun Far"},
                {text: "Kisa Far", value: "Kisa Far"},
                {text: "Donus Sinyalleri", value: "Donus Sinyalleri"},
                {text: "Tehlike Uyarisi", value: "Tehlike Uyarisi"},
                {text: "Engelliler Icin Anlam", value: "Engelliler Icin Anlam"},
                {text: "El Freni", value: "El Freni"},
                {text: "Fren Arizasi", value: "Fren Arizasi"},
                {text: "Tavan Kapagi", value: "Tavan Kapagi"},
                {text: "Yakit Seviyesi", value: "Yakit Seviyesi"},
                {text: "Motor Suyu Sogutma", value: "Motor Suyu Sogutma"},
                {text: "Akilli Sarj Durumu", value: "Akilli Sarj Durumu"},
                {text: "Motor Yagi", value: "Motor Yagi"},
                {text: "Pozisyon Lambalari", value: "Pozisyon Lambalari"},
                {text: "On Sis Lambalari", value: "On Sis Lambalari"},
                {text: "Arka Sis Lambalari", value: "Arka Sis Lambalari"},
                {text: "Park Isitma", value: "Park Isitma"},
                {text: "Motor / MI Gostergesi", value: "Motor / MI Gostergesi"},
                {text: "Servis & Bakim", value: "Servis & Bakim"},
                {text: "Sanziman Sivisi", value: "Sanziman Sivisi"},
                {text: "Sanziman Hatasi", value: "Sanziman Hatasi"},
                {text: "ABS Arizasi", value: "ABS Arizasi"},
                {text: "Asinmis Fren Arizasi", value: "Asinmis Fren Arizasi"},
                {text: "Lastik Arizasi", value: "Lastik Arizasi"},
                {text: "Ariza / Genel Ariza", value: "Ariza / Genel Ariza"},
                {text: "Motor Yagi Sicaklik Gostergesi", value: "Motor Yagi Sicaklik Gostergesi"},
                {text: "Motor Yagi Seviyesi", value: "Motor Yagi Seviyesi"},
                {text: "Motor Sogutma Suyu", value: "Motor Sogutma Suyu"},
                {text: "Direksiyon Sivisi Seviyesi", value: "Direksiyon Sivisi Seviyesi"},
                {text: "Direksiyon Arizasi", value: "Direksiyon Arizasi"},
                {text: "Yukseklik Kontrolu", value: "Yukseklik Kontrolu"},
                {text: "Retarder", value: "Retarder"},
                {text: "Motor Emisyon Sistemi", value: "Motor Emisyon Sistemi"},
                {text: "ESC Gostergesi", value: "ESC Gostergesi"},
                {text: "Fren Lambasi", value: "Fren Lambasi"},
                {text: "Koruklu Arac", value: "Koruklu Arac"},
                {text: "Durma Talebi", value: "Durma Talebi"},
                {text: "Cocuk Arabasi Istegi", value: "Cocuk Arabasi Istegi"},
                {text: "Otobus Duragi Freni", value: "Otobus Duragi Freni"},
                {text: "Yardimci Hava Basinci", value: "Yardimci Hava Basinci"},
                {text: "Hava Filtresi Tikali", value: "Hava Filtresi Tikali"},
                {text: "Yakit Filtresi Fark Basinci", value: "Yakit Filtresi Fark Basinci"},
                {text: "Emniyet Kemeri", value: "Emniyet Kemeri"},
                {text: "EBS", value: "EBS"},
                {text: "Seritten Ayrilma", value: "Seritten Ayrilma"},
                {text: "Gelismis Acil Durum Fren Sistemi", value: "Gelismis Acil Durum Fren Sistemi"},
                {text: "ACC", value: "ACC"},
                {text: "Romork Baglandi", value: "Romork Baglandi"},
                {text: "ABS Trailer 1-2", value: "ABS Trailer 1-2"},
                {text: "Airbag", value: "Airbag"}
            ],
            filterSearch: true,
        },
        {
            title: 'Toplam KM',
            dataIndex: 'totalDistance',
            key: 'totalDistance',
        },
        {
            title: 'Konum',
            dataIndex: 'gps',
            key: 'gps',
            render: (gps) => (
                gps && gps.lat && gps.lon ? (
                    <Button type="link" onClick={() => showModal(gps)}>
                        Haritada Gör
                    </Button>
                ) : (
                    <span>Tanımlanmadı</span>
                )
            ),
        }
    ];

    const generatePDF = () => {

        const doc = new jsPDF('landscape', 'mm', 'a3');
        const adjustedStartDate = new Date(infoData?.startDate);
        adjustedStartDate.setHours(adjustedStartDate.getHours() + 3);

        const adjustedEndDate = new Date(infoData?.endDate);
        adjustedEndDate.setHours(adjustedEndDate.getHours() + 3);

        const start_date = startDate ? moment.utc(startDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') : '-';
        const end_date = endDate ? moment.utc(endDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') : '-';

        const kapıNumarası = infoData?.operation_number ? infoData?.operation_number : '-';
        const chassisNumber = infoData?.chassisNumber ? infoData?.chassisNumber : '-';
        const araçPlakası = infoData?.plateNumber ? infoData?.plateNumber : '-';
        const surucuAdıSoyadı = (infoData.driverName && infoData.driverSurname && infoData.driverName !== '-' && infoData.driverSurname !== '-')
            ? `${infoData.driverName} ${infoData.driverSurname}`
            : 'Tanimlanmadi';
        const raporOluşturulmaTarihi = moment().format('DD.MM.YYYY HH:mm');
        const raporuOluşturan = user ? user?.name : '-';

        doc.setFontSize(12);

        doc.text(`Kapi Numarasi: ${kapıNumarası}`, 15, 15);
        doc.text(`Rapor Tarih Araliklari: ${start_date} / ${end_date}`, 120, 15);
        doc.text(`Arac Plakasi : ${araçPlakası}`, 15, 20);
        doc.text(`Surucu Adi/Soyadi : ${surucuAdıSoyadı}`, 120, 20);
        doc.text(`Sase Numarasi : ${chassisNumber}`, 15, 25);
        doc.text(`Hat Numarasi : Tanimlanmadi`, 120, 25);
        doc.text(`Raporu Olusturma Tarihi : ${raporOluşturulmaTarihi}`, 15, 30);
        doc.text(`Raporu Olusturan: ${raporuOluşturan}`, 120, 30);

        const formatGps = (gps) => {
            if (!gps || isNaN(gps.lat) || isNaN(gps.lon)) {
                return 'Tanimlanmadi';
            }
            return `Enlem: ${gps.lat}, Boylam: ${gps.lon}`;
        };

        const renderArizaTipi = (text) => {
            if (text === 'CONDITION_YELLOW') return 'Uyari';
            if (text === 'CONDITION_RED') return 'Ariza';
            return 'Tanimlanmadi';
        };

        const startY = 35;

        doc.autoTable({
            head: [['Olusturulma Tarih/Saat', 'Ariza Detay', 'Ariza Tipi', 'Arac Plakasi', 'Toplam KM', 'Kapi Numarasi', 'Konum']],
            body: (filteredData.length > 0 ? filteredData : tableData).map((item) => [
                (() => {
                    const date = new Date(item.date);
                    date.setHours(date.getHours() + 3); // 3 saat ekle
                    return date.toISOString().slice(0, 19).replace('T', ' ');
                })(),
                item.name || '-',
                renderArizaTipi(item.value),
                item.plateNumber || '-',
                item.totalDistance || '-',
                item.operationNumber || '-',
                formatGps(item.gps)
            ]),
            startY: startY,
        });

        doc.save('Alarm_Raporu.pdf');
    };

    const handleExcel = () => {
        const dataToExport = filteredData.length > 0 ? filteredData : tableData;

        const sanitizedOperationNumber = vehicle_id === true ? 'Hepsi' : infoData.operation_number ? infoData.operation_number.replace(/\n/g, '') : 'Tanımlanmadı';
        const plateNumber = vehicle_id === true ? 'Hepsi' : infoData.plateNumber || 'Tanımlanmadı';
        const chassisNumber = vehicle_id === true ? 'Hepsi' : infoData.chassisNumber || 'Tanımlanmadı';
        const start_date = startDate ? moment.utc(startDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') : '-';
        const end_date = endDate ? moment.utc(endDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') : '-';
        const reportCreationDate = moment().format('DD/MM/YYYY HH:mm:ss');
        const driver = (infoData.driverName && infoData.driverSurname && infoData.driverName !== '-' && infoData.driverSurname !== '-')
            ? `${infoData.driverName} ${infoData.driverSurname}`
            : 'Tanımlanmadı';

        const processedData = dataToExport.map((item) => {
            const date = new Date(item.date);
            date.setHours(date.getHours() + 3);
            const adjustedDate = date.toISOString().slice(0, 19).replace('T', ' ');

            // Enlem ve boylam bilgileri ile "Haritada Gör" linki
            let location = 'Tanımlanmadı';
            if (item.gps && item.gps.lat && item.gps.lon) {
                const googleMapsLink = `https://www.google.com/maps?q=${item.gps.lat},${item.gps.lon}`;
                location = {text: 'Haritada Gör', hyperlink: googleMapsLink};
            }

            const renderArizaTipi = (text) => {
                if (text === 'CONDITION_YELLOW') return 'Uyari';
                if (text === 'CONDITION_RED') return 'Ariza';
                return 'Tanimlanmadi';
            };

            return {
                'Oluşturulma Tarihi': adjustedDate,
                'Arıza Detay': item.name,
                'Arıza Tipi': renderArizaTipi(item.value),
                'Araç Plakası': item.plateNumber,
                'Toplam KM': item.totalDistance,
                'Kapı Numarası': item.operationNumber,
                'Konum': location,
            };
        });

        // Çalışma sayfasını oluşturma ve başlık ekleme
        const worksheet = XLSX.utils.json_to_sheet([]);
        XLSX.utils.sheet_add_aoa(worksheet, [
            ['Kapı Numarası:', sanitizedOperationNumber, '', 'Rapor Tarih Aralıkları:', `${start_date} - ${end_date}`],
            ['Araç Plaka Numarası:', plateNumber, '', 'Sürücü Adı/Soyadı:', driver],
            ['Şase Numarası:', chassisNumber, '', 'Hat Numarası:', 'Tanımlanmadı'],
            ['Rapor Oluşturulma Tarihi:', reportCreationDate, '', 'Rapor Oluşturan:', user?.name],
        ], {origin: 'A1'});

        // İşlenen veriyi ekleyerek 'Konum' için link oluşturma
        XLSX.utils.sheet_add_json(worksheet, processedData, {origin: 'A6', skipHeader: false});

        // Linkler için "Konum" sütununu özel olarak işle
        processedData.forEach((item, index) => {
            if (item.Konum.hyperlink) {
                const rowNumber = index + 6;  // Verinin başladığı satırdan itibaren
                worksheet[`G${rowNumber}`] = {f: `HYPERLINK("${item.Konum.hyperlink}", "${item.Konum.text}")`};
            }
        });

        // Çalışma kitabını ve dosya adı ayarları
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Alarm Raporu');
        XLSX.writeFile(workbook, 'Alarm_Raporu.xlsx');
    };

    const onChange = (pagination, filters, sorter, extra) => {
        setFilteredData(extra?.currentDataSource)
    };

    return (
        <Row justify="center">
            <Col xs={24} sm={24} md={24} lg={24} xl={16} xxl={12}>
                <Card title="Alarm Raporu" type="inner" size="small" style={{marginBottom: 10}}>
                    <SelectVehicle name="vehicleID" onChange={(e) => setVehicleID(e)}/>
                    <Form.Item name="byDate" label="Tarih Aralığı">
                        <RangePicker
                            showTime
                            disabled={isLoading}
                            disabledDate={disabledDate}
                            placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
                            style={{margin: "auto", marginRight: 3, width: "100%"}}
                            onChange={handleDateChange}
                        />
                    </Form.Item>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined/>}
                        onClick={handleQuery}
                        style={{marginTop: '16px'}}
                        loading={isLoading}
                        disabled={!vehicle_id || !startDate || !endDate || isLoading}
                    >
                        Rapor Oluştur
                    </Button>
                </Card>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={20} xxl={24}>
                <Card
                    type="inner"
                    title="Sonuçlar"
                    size="small"
                    extra={
                        <>
                            <Button
                                style={{marginRight: 5}}
                                size="small"
                                disabled={!tableData.length || isLoading}
                                type="primary"
                                icon={<FileTextOutlined/>}
                                onClick={handleExcel}
                            >Excel</Button>
                            <Button
                                size="small"
                                disabled={!tableData.length || isLoading || vehicle_id === true}
                                type="primary"
                                icon={<FilePdfOutlined/>}
                                onClick={generatePDF}
                            >Pdf</Button>
                        </>
                    }
                >
                    <Table
                        rowKey={() => `${Math.random().toString(36).substr(2, 9)}-${Date.now()}`}
                        loading={isLoading}
                        bordered={true}
                        size="small"
                        columns={columns}
                        dataSource={tableData}
                        error={error}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            onChange: onPageChange,
                        }}
                        onChange={onChange}
                    />
                </Card>
                {selectedGps && (
                    <AlarmMapModal gps={selectedGps} visible={isModalVisible} onClose={closeModal}/>
                )}
            </Col>
        </Row>
    );

};

export default AlarmReport;
